import { saveAs } from 'file-saver-es';
import { mergeMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { RouterSelectors } from '@mitsubishi/core';
import {
  PerformanceMessage,
  RequestLogRequest,
  RequestLogService,
  SelectionRequestLogScope,
  StudyRequestLogScope,
} from '@mitsubishi/shared/data-access';
import { MiscUtils } from '@mitsubishi/utils';

import { EntityCollections } from '@angular-ru/cdk/entity';
import { Action, State, StateContext, Store } from '@ngxs/store';

import { DatasheetGroup, EditDatasheetRequest, Selection } from '../../models';
import { DatasheetService } from '../../services';
import { DatasheetFormlyUtils } from '../../utils/datasheet-formly.utils';
import { DatasheetActions } from './datasheet.actions';

export interface DatasheetStateModel {
  groups: DatasheetGroup[];
  messages: PerformanceMessage[];
  groupId?: number;
  sectionId?: string;
  rowKey?: string;
  form?: UntypedFormGroup;
  isEditing: boolean;
  editedValues: Record<string, unknown>;
  hasAttemptedAutoCalculation: boolean;
}

const defaultState: DatasheetStateModel = {
  groups: [],
  messages: [],
  groupId: undefined,
  sectionId: undefined,
  rowKey: undefined,
  form: new UntypedFormGroup({}),
  isEditing: false,
  editedValues: {},
  hasAttemptedAutoCalculation: false,
};

@State<DatasheetStateModel>({
  name: 'datasheet',
  defaults: defaultState,
})
@Injectable()
export class DatasheetState {
  constructor(
    private store: Store,
    private datasheetService: DatasheetService,
    private requestLogService: RequestLogService
  ) {}

  @Action(DatasheetActions.CalculateDatasheet)
  calculateSelectionDatasheet(ctx: StateContext<DatasheetStateModel>) {
    const projectId = this.getCurrentProjectId();
    const unitId = this.getCurrentUnitId();
    const performanceId = this.getCurrentPerformanceId();

    // Reset state but preserve the auto-calculation flag to prevent infinite loops
    const currentState = ctx.getState();
    ctx.setState({
      ...defaultState,
      hasAttemptedAutoCalculation: currentState.hasAttemptedAutoCalculation,
    });

    let calculate;

    if (performanceId) {
      calculate = this.datasheetService.calculateStudyDatasheet({
        projectId,
        unitId,
        performanceId,
      });
    } else {
      calculate = this.datasheetService.calculateSelectionDatasheet(projectId, unitId);
    }

    return calculate.pipe(mergeMap(() => ctx.dispatch(new DatasheetActions.LoadDatasheet(false))));
  }

  @Action(DatasheetActions.LoadDatasheet)
  loadSelectionDatasheet(ctx: StateContext<DatasheetStateModel>, { sendEditedTables }: DatasheetActions.LoadDatasheet) {
    const projectId = this.getCurrentProjectId();
    const unitId = this.getCurrentUnitId();
    const performanceId = this.getCurrentPerformanceId();

    let editedValues: EditDatasheetRequest = {
      performances: {},
      workingConditions: {},
    };
    const state = ctx.getState();
    if (sendEditedTables) {
      editedValues = {
        performances: this.computeEditablePerformanceValues(state),
        workingConditions: this.computeEditableWorkingConditionsValues(state),
      };
    }
    return (
      performanceId
        ? this.datasheetService.getStudyUnitDatasheet(projectId, performanceId, unitId, editedValues)
        : this.datasheetService.getSelectionUnitDatasheet(projectId, unitId, editedValues)
    ).pipe(
      tap(datasheet => {
        if (!sendEditedTables) {
          ctx.patchState({
            form: new UntypedFormGroup({}),
            rowKey: undefined,
            isEditing: false,
            editedValues: {},
          });
        }

        ctx.patchState({ groups: datasheet.groups, messages: datasheet.messages });
        if (!state.groupId || !state.sectionId) {
          if (datasheet.groups.length > 0) {
            const groupId = datasheet.groups[0].id;
            if (datasheet.groups[0].sections.length > 0) {
              const sectionId = datasheet.groups[0].sections[0].id;
              ctx.patchState({ groupId: groupId, sectionId: sectionId });
            }
          }

          // If datasheet is empty and doesn't contain errors it means it must still be calculated so we calculate it for the user automatically
          // Only attempt auto-calculation once to prevent infinite loops
          if (datasheet.groups.length === 0 && datasheet.messages.length === 0 && !state.hasAttemptedAutoCalculation) {
            ctx.patchState({ hasAttemptedAutoCalculation: true });
            ctx.dispatch(new DatasheetActions.CalculateDatasheet());
          }
        }
      })
    );
  }

  @Action(DatasheetActions.SaveDatasheet)
  saveDatasheet(ctx: StateContext<DatasheetStateModel>) {
    const projectId = this.getCurrentProjectId();
    const unitId = this.getCurrentUnitId();
    const performanceId = this.getCurrentPerformanceId();

    const state = ctx.getState();
    const editedValues: EditDatasheetRequest = {
      performances: this.computeEditablePerformanceValues(state),
      workingConditions: this.computeEditableWorkingConditionsValues(state),
    };

    return (
      performanceId
        ? this.datasheetService.saveStudyDatasheet(projectId, performanceId, unitId, editedValues)
        : this.datasheetService.saveSelectionDatasheet(projectId, unitId, editedValues)
    ).pipe(mergeMap(() => ctx.dispatch(new DatasheetActions.LoadDatasheet(false))));
  }

  @Action(DatasheetActions.SetSelectedGroupAndSection)
  setSelectedGroup(
    ctx: StateContext<DatasheetStateModel>,
    { groupId, sectionId }: DatasheetActions.SetSelectedGroupAndSection
  ) {
    if (ctx.getState().isEditing) {
      return;
    }
    return ctx.patchState({ groupId: groupId, sectionId: sectionId, rowKey: undefined });
  }

  @Action(DatasheetActions.SetSelectedRow)
  setSelectedRow(ctx: StateContext<DatasheetStateModel>, { rowKey }: DatasheetActions.SetSelectedRow) {
    if (ctx.getState().rowKey === rowKey) {
      return ctx.patchState({ rowKey: undefined });
    } else {
      return ctx.patchState({ rowKey: rowKey });
    }
  }

  @Action(DatasheetActions.ClearState)
  clearState(ctx: StateContext<DatasheetStateModel>) {
    return ctx.setState(defaultState);
  }

  @Action(DatasheetActions.DownloadDatasheetRequestLog)
  downloadDatasheetRequestLog(ctx: StateContext<DatasheetStateModel>) {
    const unitId = this.getCurrentUnitId();
    const workingConditionId = this.getCurrentWorkingConditionId();

    if (workingConditionId) {
      const request: RequestLogRequest = {
        studyUnitId: unitId,
        studyWorkingConditionId: workingConditionId,
        scope: StudyRequestLogScope.DATASHEET,
      };
      return this.requestLogService.getRequestLog(request);
    }

    const request: RequestLogRequest = {
      unitId: unitId,
      scope: SelectionRequestLogScope.DATASHEET,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(DatasheetActions.UpdateSectionPdfVisibility)
  updateSectionPdfVisibility(
    ctx: StateContext<DatasheetStateModel>,
    { datasheetNotes, sections }: DatasheetActions.UpdateSectionPdfVisibility
  ) {
    const projectId = this.getCurrentProjectId();
    const unitId = this.getCurrentUnitId();
    const workingConditionId = this.getCurrentWorkingConditionId();

    const payload = {
      sectionIdList: sections,
      datasheetNote: datasheetNotes,
    };
    if (workingConditionId) {
      return this.datasheetService.updateStudySectionPdfVisibility(projectId, workingConditionId, unitId, payload);
    } else {
      return this.datasheetService.updateSelectionSectionPdfVisibility(projectId, unitId, payload);
    }
  }

  @Action(DatasheetActions.DownloadSelectionDatasheetPdfRequestLog)
  downloadSelectionDatasheetPdfRequestLog(ctx: StateContext<DatasheetStateModel>) {
    const unitId = this.getCurrentUnitId();

    const request: RequestLogRequest = {
      unitId: unitId,
      scope: SelectionRequestLogScope.DATASHEET_PDF,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(DatasheetActions.CreateStudyUnitPdf)
  datasheetCreateStudyUnitPdf(
    ctx: StateContext<EntityCollections<Selection>>,
    { studySubcategoryId, requestTraceId }: DatasheetActions.CreateStudyUnitPdf
  ) {
    const projectId = this.getCurrentProjectId();
    const studyId = this.getCurrentStudyId();
    const unitId = this.getCurrentUnitId();
    const performanceId = this.getCurrentPerformanceId();
    return this.datasheetService.generateStudyUnitDatasheetPDF(
      projectId,
      studyId,
      studySubcategoryId,
      performanceId,
      unitId,
      requestTraceId
    );
  }

  @Action(DatasheetActions.DownloadStudyDatasheetPdfRequestLog)
  downloadStudyDatasheetPdfRequestLog(ctx: StateContext<DatasheetStateModel>) {
    const unitId = this.getCurrentUnitId();
    const workingConditionId = this.getCurrentWorkingConditionId();

    const request: RequestLogRequest = {
      studyUnitId: unitId,
      studyWorkingConditionId: workingConditionId,
      scope: StudyRequestLogScope.DATASHEET_PDF,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(DatasheetActions.SetIsEditing)
  isEditing({ patchState }: StateContext<DatasheetStateModel>, { isEditing }: DatasheetActions.SetIsEditing) {
    patchState({ isEditing });
  }

  @Action(DatasheetActions.SetEditedValues)
  setEditedValues({ patchState }: StateContext<DatasheetStateModel>, { values }: DatasheetActions.SetEditedValues) {
    patchState({ editedValues: values });
  }

  @Action(DatasheetActions.DownloadPartialLoads)
  downloadPartialLoads(ctx: StateContext<DatasheetStateModel>) {
    const projectId = this.getCurrentProjectId();
    const unitId = this.getCurrentUnitId();
    const performanceId = this.getCurrentPerformanceId();

    const state = ctx.getState();
    const editedValues: EditDatasheetRequest = {
      performances: this.computeEditablePerformanceValues(state),
      workingConditions: this.computeEditableWorkingConditionsValues(state),
    };

    return (
      performanceId
        ? this.datasheetService.downloadPartialLoadsStudy(projectId, performanceId, unitId, editedValues)
        : this.datasheetService.downloadPartialLoadsSelection(projectId, unitId, editedValues)
    ).pipe(
      tap(resp => {
        const blob = new Blob([resp.body as Blob], { type: 'application/octet-stream;charset=UTF-8' });
        saveAs(blob, MiscUtils.extractFileNameFromHttpResponse(resp));
      })
    );
  }

  private getCurrentProjectId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).projectId;
  }

  private getCurrentStudyId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).studyId;
  }

  private getCurrentUnitId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).unitId;
  }

  private getCurrentWorkingConditionId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).workingConditionId;
  }

  private getCurrentPerformanceId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).performanceId;
  }

  private computeEditablePerformanceValues(state: DatasheetStateModel): any {
    const keys = DatasheetFormlyUtils.getEditablePerformanceKeys(state.groups);
    return DatasheetFormlyUtils.computeEditablePerformanceValues(state.form, state.editedValues, keys);
  }

  private computeEditableWorkingConditionsValues(state: DatasheetStateModel): any {
    const keys = DatasheetFormlyUtils.getEditableWorkingConditionsKeys(state.groups);
    return DatasheetFormlyUtils.computeEditableWorkingConditionsValues(state.form, state.editedValues, keys);
  }
}
